import * as PopoverPrimitive from '@radix-ui/react-popover'
import {PopoverContentProps} from '@radix-ui/react-popover'
import {ScrollAreaProps} from '@radix-ui/react-scroll-area'
import {Box, Checkbox} from '@viptech/react-components'
import {Command as CommandPrimitive} from 'cmdk'
import {createContext, forwardRef, useContext, useEffect, useMemo, useState} from 'react'
import ArrowDownIcon from 'src/images/ArrowDownIcon'
import styled from 'styled-components'
import {Command, CommandEmpty, CommandInput, CommandItem, CommandList, CommandLoading} from '../command/Command'
import {InputLabel} from '../input/InputBase'
import Loading from '../Loading'
import {Popover, PopoverTrigger} from '../popover/Popover'
import {ScrollArea} from '../scroll-area/ScrollArea'

type ContextData = {
  name?: string
  open: boolean
  loading?: boolean
  disabled?: boolean
  shouldFilter?: boolean
  value: Array<SelectValue>
  onChangeValue: (value: SelectValue) => void
  handleOpen: () => void
}

const Context = createContext({} as ContextData)

const useMultiSelect = () => {
  return useContext(Context)
}

interface MultiSelectProps {
  name?: string
  loading?: boolean
  disabled?: boolean
  shouldFilter?: boolean
  value?: Array<SelectValue>
  defaultValue?: Array<SelectValue>
  onChange?: (value: Array<SelectValue>) => void
  onClose?: () => void
}

export interface SelectValue {
  value: string
  label: string
}

const MultiSelect = ({
  name,
  value: _value,
  disabled,
  loading,
  defaultValue,
  shouldFilter,
  children,
  onClose,
  onChange,
}: React.PropsWithChildren<MultiSelectProps>) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<Array<SelectValue>>(defaultValue ?? [])

  const memoizedValue = useMemo(
    () => ({
      name,
      open,
      value,
      loading,
      disabled,
      shouldFilter,
      onChangeValue: (selectedValue: SelectValue) => {
        const exists = value.some((v) => v.value === selectedValue.value)
        if (onChange) onChange(exists ? value.filter((v) => v.value !== selectedValue.value) : [...value, selectedValue])
        else setValue(exists ? value.filter((v) => v.value !== selectedValue.value) : [...value, selectedValue])
      },
      handleOpen: () => setOpen((prev) => !prev),
    }),
    [disabled, loading, name, onChange, open, shouldFilter, value],
  )

  const handleOpen = (open: boolean) => {
    setOpen(open)
    if (!open && onClose) onClose()
  }

  useEffect(() => {
    if (_value) setValue(_value)
  }, [_value])

  return (
    <Popover open={open} onOpenChange={handleOpen} modal>
      <Context.Provider value={memoizedValue}>{children}</Context.Provider>
    </Popover>
  )
}

interface MultiSelectValueProps extends React.HTMLAttributes<HTMLButtonElement> {
  placeholder?: string
  showTags?: boolean
  borderColor?: string
  width?: string
  maxWidthToBreakLabel?: string
  showFloatingLabel?: boolean
}

interface MultiSelectLabelProps {
  label: string
  disabled?: boolean
  labelColor?: string
  labelFontSize?: string
  labelFontFamily?: string
  labelFontWeight?: string
}

const MultiSelectLabel: React.FC<MultiSelectLabelProps> = ({label, disabled, labelColor, labelFontSize, labelFontFamily, labelFontWeight}) => {
  return (
    <InputLabel disabled={disabled} labelColor={labelColor} labelFontSize={labelFontSize} labelFontFamily={labelFontFamily} labelFontWeight={labelFontWeight}>
      {label}
    </InputLabel>
  )
}

const MultiSelectValue = forwardRef<HTMLButtonElement, MultiSelectValueProps>(
  ({placeholder, showTags = true, borderColor, width, maxWidthToBreakLabel, showFloatingLabel = false}, forwardedRef) => {
    const {name, open, disabled, value, onChangeValue, handleOpen} = useMultiSelect()

    const shrink = open || value.length > 0

    const handleDelete = (value: SelectValue) => {
      onChangeValue(value)
    }

    const formatItemLabel = (label: string, isLastPosition: boolean) => {
      return isLastPosition ? label : `${label}, `
    }

    return (
      <WrapperWithLabel width={width}>
        {showFloatingLabel && (
          <FloatingLabel htmlFor={name} shrink={shrink} disabled={disabled}>
            {placeholder}
          </FloatingLabel>
        )}

        <PopoverTrigger asChild onClick={handleOpen}>
          <WrapperButtonSelect id={name} name={name} ref={forwardedRef} disabled={disabled === true} width='100%' borderColor={borderColor ?? '#D0D5DD'}>
            <div style={{display: 'flex', width: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: '5px'}}>
              {value.length === 0 || !showTags
                ? !showFloatingLabel && placeholder
                : value.map((selectValue, index) => (
                    <ShowLabelsSelectedDiv key={selectValue.value}>
                      <ShowLabelSelectedSpan maxWidth={maxWidthToBreakLabel}>
                        {formatItemLabel(selectValue.label, index === value.length - 1)}
                      </ShowLabelSelectedSpan>
                      {!disabled && (
                        <div
                          style={{marginRight: '5px'}}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(selectValue)
                          }}
                        />
                      )}
                    </ShowLabelsSelectedDiv>
                  ))}
            </div>

            <div style={{position: 'absolute', right: 12}}>
              <ArrowDownIcon transform={open ? 'rotate(180deg)' : 'rotate(0deg)'} />
            </div>
          </WrapperButtonSelect>
        </PopoverTrigger>
      </WrapperWithLabel>
    )
  },
)
MultiSelectValue.displayName = 'MultiSelectValue'

const WrapperButtonSelect = styled.button<{borderColor: string; disabled: boolean; width: string}>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  border-radius: 8px;
  border: ${({borderColor}) => `1px solid ${borderColor} `};
  background-color: white;
  color: #98a2b3;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({disabled}) => (disabled ? 0.5 : 1)};
  min-height: 40px;
  max-height: 40px;

  width: ${({width}) => width};
  position: relative;
  overflow: hidden;
`
const ShowLabelsSelectedDiv = styled.div`
  font-size: 13px;
  color: #344054;
  display: flex;
  align-items: center;
`

const ShowLabelSelectedSpan = styled.span<{maxWidth?: string}>`
  max-width: ${({maxWidth}) => (maxWidth ? maxWidth : '150px')};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const WrapperWithLabel = styled.div<{width?: string}>`
  position: relative;
  width: ${({width}) => width ?? '100%'};
`

const FloatingLabel = styled.label<{shrink: boolean; disabled?: boolean}>`
  position: absolute;
  left: 12px;
  z-index: 1;
  top: ${({shrink}) => (shrink ? '-6px' : '50%')};
  transform: translateY(${({shrink}) => (shrink ? '0' : '-50%')});
  font-size: ${({shrink}) => (shrink ? '10px' : '14px')};
  color: '#9aa4b2';
  background: white;
  padding: 0 4px;
  transition: all 0.15s ease;
  pointer-events: none;
  letter-spacing: 0.5px;
`

const MultiSelectContent = ({className, children, ...rest}: PopoverContentProps) => {
  const {shouldFilter} = useMultiSelect()
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        style={{
          marginTop: '0.5rem',
          padding: '0.4rem',
          position: 'relative',
          zIndex: 50,
          display: 'flex',
          height: '100%',
          maxHeight: '24rem',
          minWidth: '8rem',
          flexDirection: 'column',
          borderRadius: '0.375rem',
          backgroundColor: 'white',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          width: 'var(--radix-popper-anchor-width)',
        }}
        {...rest}>
        <Command shouldFilter={shouldFilter}>{children}</Command>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

const MultiSelectScrollView = ({children, ...rest}: ScrollAreaProps) => {
  const {loading} = useMultiSelect()
  return (
    <ScrollArea
      {...rest}
      style={{
        display: 'flex',
        maxHeight: '300px',
        width: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
      <CommandList style={{overflowY: 'auto', overflowX: 'hidden'}}>
        {loading && (
          <CommandLoading>
            <Box padding='8px 0'>
              <Loading height='16px' width='16px' />
            </Box>
          </CommandLoading>
        )}
        {!loading && <CommandEmpty>Nenhum resultado encontrado</CommandEmpty>}
        {children}
      </CommandList>
    </ScrollArea>
  )
}

const MultiSelectInput = forwardRef<React.ElementRef<typeof CommandPrimitive.Input>, React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>>(
  (props, ref) => {
    return <CommandInput ref={ref} {...props} />
  },
)
MultiSelectInput.displayName = 'Searchable'

interface MultiSelectItemProps extends React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> {
  value: string
  label: string
}

const MultiSelectItem = forwardRef<React.ElementRef<typeof CommandPrimitive.Item>, MultiSelectItemProps>(
  ({children, value: itemValue, label: itemLabel, ...rest}, forwardedRef) => {
    const {value, onChangeValue} = useMultiSelect()
    const selectValue = value.find((v) => v.value === itemValue)
    const [isCheck, setIsCheck] = useState(!!selectValue)

    const handleSelect = () => {
      onChangeValue({value: itemValue, label: itemLabel})
    }

    useEffect(() => {
      const selectValue = value.find((v) => v.value === itemValue)
      setIsCheck(!!selectValue)
    }, [value])

    return (
      <CommandItem
        ref={forwardedRef}
        onMouseDown={(e) => e.preventDefault()}
        onSelect={handleSelect}
        value={`${itemValue} - ${itemLabel}`}
        aria-selected={selectValue?.value === itemValue}
        {...rest}>
        <Checkbox checked={isCheck} onChange={() => handleSelect()} />
        <span style={{width: '100%'}}>{children}</span>
      </CommandItem>
    )
  },
)
MultiSelectItem.displayName = 'MultiSelectItem'

const MultiSelectEmpty = ({children}: React.PropsWithChildren<{}>) => {
  return <div style={{textAlign: 'center', fontSize: '0.875rem', color: 'gray'}}>{children}</div>
}

export {MultiSelect, MultiSelectContent, MultiSelectEmpty, MultiSelectInput, MultiSelectItem, MultiSelectLabel, MultiSelectScrollView, MultiSelectValue}
